/* eslint-disable no-undef */
import API from 'constants/api';
import { SET_LOADING, SET_SAVED, SET_FIELDS, SET_ACTIVE_TAB, MASSEDIT_ADD, MASSEDIT_ALLOW,
  RESET_STATE, SET_PARAMETERS, SET_PRIMARY_VERSION, SET_OBJECT, SET_OBJECT_COUNT } from 'constants/actionTypes';
import Predicates from 'constants/predicates';
import Types from 'constants/types';
import { bindObjectToStore } from 'reducers/store';
import { getObjectFields } from 'helpers/objectHelpers';
import { getUnsavedChanges } from 'validation/objectValidation';
import { fetchMetadata, getFormData, 
  resToJsonHandleErrors, actionError } from 'helpers/actionHelpers';
import { convertViewToFormat } from 'helpers/dataHelpers';
import { redirectIFrame } from 'helpers/iframeHelpers';
import { checkUniquenessAndPromptUser } from 'actions/validationActions';
import update from 'immutability-helper';
import { LS } from 'constants/localization';

/**
 * Load object from REST API -> JSON -> state
 * then, load enums for object
 * @param {*} url REST API url
 * @param {*} mapObject Override changes to object (example: remove minOccurs from fields)
 */
export const getObject = (url, mapObject) => dispatch => {
  dispatch({ type: SET_LOADING, value: true });
  return fetchMetadata(url)
    .then(object => bindObjectToStore(object, mapObject))
    .finally(() => dispatch({ type: SET_LOADING, value: false }));
};

/**
 * Set parameters from JSP model (eg. uri, parentUri, view)
 * @param parameters Parameters to set from JSP
 */
export const setObjectParameters = (parameters) => dispatch => {
  dispatch({ type: SET_PARAMETERS, parameters });
};

/**
 * Primary version: image displayed in Search Results
 */
export const setPrimaryVersion = (uri) => dispatch => {
  dispatch({ type: SET_PRIMARY_VERSION, primaryVersion: uri });
};

export const getDisplayObject = (uri, mode) => dispatch => {
  let url = `${API.DISPLAY_OBJECT}?uri=${uri}`;
  if (mode === 'print') {
    url = url.concat('&printMode');
  }
  dispatch({ type: SET_LOADING, value: true });
  return fetchMetadata(url)
    .then(object => {
      if (object.tabs?.length > 0) {
        setSenseID(object.tabs[0], uri);
      }
      dispatch({ type: SET_OBJECT, object });
    })
    .finally(() => dispatch({ type: SET_LOADING, value: false }));
};

// Add SenseID field to first tab - first group
const setSenseID = (tab, id) => {
  if (tab.groups && tab.groups.length > 0) {
    tab.groups[0].fields.push({
      label: 'SenseID',
      values: [id],
      type: Types.XSD.STRING
    });
  }
};

export const removeItemFromCart = (uri) => {
  const url = 'cart.do';
  const formData = new URLSearchParams();
  formData.append('action', 'removeItemInOrderFormPage');
  formData.append('removeItem', uri);
  return fetch(url, {
    method: 'POST',
    body: formData
  })
    .then(res => {
      if (res.status === 200) {
        redirectIFrame('cart.do?action=viewCart');
      }
    });
};

/**
 * POST to CreateArchivedObjectController
 */
export const createObject = (message) => (dispatch, getState) => {
  const state = getState();
  const formData = getFormData(state.field);
  formData.set('parentUri', state.object.parentUri);
  formData.set('view', state.object.view);
  formData.set('templateItem', state.object.uri);
  
  if (state.object.namedTemplate) {
    formData.set('namedTemplate', state.object.namedTemplate);
  }
  if (state.object.objectCount && state.object.objectCount != '' && state.object.objectCount != '1') {
    formData.set('objectsToCreate', state.object.objectCount);
  }

  const view = state.object.view;
  const fields = state.field.fields;
  const oldFields = state.field.originalValues;
  checkUniquenessAndPromptUser(convertViewToFormat(view), fields, oldFields,'non-existing-uri', message)
    .then(allowed => {
      if (allowed) {
        return fetch(API.DAM.CREATE_OBJECT, {
          method: 'POST',
          body: formData,
        })
          .then(resToJsonHandleErrors)
          .then(res => {
            if (res.uri) {
              redirectIFrame(`${API.DAM.EDIT_OBJECT}?uri=${res.uri}`);
            } else {
              actionError({ message: 'saveFailed' });
            }
          });
      }
    });
};

/**
 * Load named template on create
 */
export const setTemplate = (uri) => (dispatch, getState) => {
  const view = getState().object.view;
  let fields = getState().field.fields;
  
  fetchMetadata(`${API.UI.NAMED_TEMPLATE}&uri=${uri}&view=${view}`)
    .then(template => {
      // Overwrite values from template
      for (let field of getObjectFields(template.tabs)) {
        if (field.values.length === 0) {
          continue;
        }
        fields = update(fields, { [field.predicate]: {
          values: { $set: field.values }
        }
        });
      }
      dispatch({ type: SET_FIELDS, fields });
      dispatch(setObjectParameters({ namedTemplate: template.uri }));
    });
};

/**
 * Get named templates select options for Folder on create
 */
export const getTemplateOptions = folder => {
  return fetch(`${API.UI.TEMPLATE_OPTIONS}&folder=${folder}`)
    .then(resToJsonHandleErrors)
    .catch(e => console.log(e));
};

/**
 * Copy object in edit view
 */
export const copyObject = () => (dispatch, getState) => {
  if (confirm('Oletko varma, että haluat kopioida objektin?')) {
    const state = getState();
  
    const uri = state.object.uri;
    const folder = state.object.parentFolder;
    let url = `${API.DAM.COPY_OBJECT}?uri=${uri}&folder=${folder}`;
    const windowId = sessionStorage.getItem('WINDOW_ID');
    if (!windowId) {
      windowId = Date.now();
      sessionStorage.setItem('WINDOW_ID', windowId);
    }
    url = url + '&windowId=' + windowId;
  
    return fetch(url)
      .then(resToJsonHandleErrors)
      .then(res => {
        if (res.returnUrl) {
          redirectIFrame(res.returnUrl);
        }
      })
      .catch(e => console.log(e));
  }
};

/**
 * Autosave changes for user session
 */
export const autosaveObject = () => (dispatch, getState) => {
  const timeoutSeconds = 60 * 45;

  return setInterval(() => {
    const check = getUnsavedChanges()(dispatch, getState);
    if (check.changed) {
      // autosave does not trigger refresh
      editObject({ autosave: true })(dispatch, getState);
    }
  }, timeoutSeconds * 1000);
};

/**
 * POST to EditObjectController
 */
export const editObject = (parameters, message) => (dispatch, getState) => {
  const state = getState();
  const formData = getFormData(state.field);
  formData.set('uri', state.object.uri);
  const windowId = sessionStorage.getItem('WINDOW_ID');
  if (!windowId) {
    windowId = Date.now();
    sessionStorage.setItem('WINDOW_ID', windowId);
  }
  formData.set('windowId', windowId);

  const primaryVersion = state.object.primaryVersion;
  if (primaryVersion) {
    formData.set(Predicates.ARCHIVE.PRIMARY_VERSION, primaryVersion);
  }
  parameters = new URLSearchParams(parameters).toString();

  const view = state.object.view;
  const fields = state.field.fields;
  const oldFields = state.field.originalValues;

  checkUniquenessAndPromptUser(convertViewToFormat(view), fields, oldFields, state.object.uri, message)
    .then(res => {
      if (res) {
        return fetch(`${API.DAM.EDIT_OBJECT}?${parameters}`, {
          method: 'POST',
          body: formData
        })
          .then(resToJsonHandleErrors)
          .then(result => {
            if (result.returnUrl) {
              dispatch({ type: SET_SAVED });
              redirectIFrame(result.returnUrl);
            } else {
              actionError({ message: 'saveFailed' });
            }
          });
      }
    });
};

export const sortSearchResults = (predicate, isAscending) =>  {
  const formData = new URLSearchParams();
  formData.set('sortTable', 'true');
  formData.set('isResults', 'true');
  formData.set('windowId', sessionStorage.getItem('WINDOW_ID'));
 
  return fetch('search.do?orderByPredicate='+predicate+'&orderIsAscending='+isAscending, {
    method: 'POST',
    body: formData
  })
    .then(res => {
      if (res.redirected) {
        redirectIFrame(res.url);
      }
    })
    .catch(e => console.log(e));
};

/**
 * POST to NamedMetataTemplateController
 */
export const createNamedMetadataTemplate = e => (dispatch, getState) => {
  e.preventDefault();
  const state = getState();
  const formData = getFormData(state.field);
  formData.set('uri', state.object.uri);
  formData.set('parent', state.object.parentUri);

  return fetch(API.DAM.NAMED_TEMPLATE, {
    method: 'POST',
    body: formData,
  })
    .then(res => {
      if (res.status === 200) {
        if (res.redirected) {
          dispatch({ type: SET_SAVED });
          redirectIFrame(res.url);
        }
      } else {
        res.json().then(actionError);
      }
    });
};

/*
  Retrieve formatted Search Results from a List of URIs
  formatted by FolderFormat
*/
export const getSearchResultObjects = (objects) => {
  const data = new FormData();
  objects.forEach(o => {
    data.append('uris', o);
  });

  return fetch(`${API.SEARCH_RESULTS}`, { method: 'POST', body: data })
    .then(resToJsonHandleErrors);
};

/**
 * POST to EditMultipleObjectsController
 */
export const massEditObjects = e => (dispatch, getState) => {
  e.preventDefault();
  const state = getState();
  const formData = getFormData(state.field);
  formData.set('uri', state.object.uri);

  // Set allowed fields from checkboxes (user may delete old values)
  Object.entries(state.massedit.allowed)
    .filter(([_, allowed]) => allowed)
    .forEach(([predicate]) => {
      formData.set(MASSEDIT_ALLOW + predicate, 'on');
    });

  Object.entries(state.massedit.added)
    .filter(([_, canAdd]) => canAdd)
    .forEach(([predicate]) => {
      formData.set(MASSEDIT_ADD + predicate, 'on');
    });

  if (isMassEditConfirmMessageShown(formData)) {
    if (!window.confirm(LS.prompt.massEditOverwriteField)) return;
  }

  return fetch(API.DAM.MASS_EDIT, {
    method: 'POST',
    body: formData
  })
    .then(res => {
      if (res.status === 200) {
        if (res.redirected) {
          redirectIFrame(res.url);
        }
      } else {
        res.json().then(actionError);
      }
    });
};

const isMassEditConfirmMessageShown = formData => {
  const masseditAllowPredicates = [];
  const masseditAddPredicates = [];
  for (const entry of formData.entries()) {
    if (entry[0].startsWith(MASSEDIT_ALLOW)) {
      masseditAllowPredicates.push(entry[0].replace(MASSEDIT_ALLOW, ''));
    } else if (entry[0].startsWith(MASSEDIT_ADD)) {
      masseditAddPredicates.push(entry[0].replace(MASSEDIT_ADD, ''));
    }
  }

  return masseditAllowPredicates.reduce((returnValue, next) => {
    if (returnValue) return returnValue;
    return !masseditAddPredicates.includes(next);
  }, false);
};

export const setObjectCount = (value) => dispatch => {
	
  dispatch({ 
    type: SET_OBJECT_COUNT, 
    objectCount: value 
  });
};

// Remember active tab when switching between views
export const setActiveTab = value => dispatch => {
  dispatch({
    type: SET_ACTIVE_TAB,
    value
  });
};

// Clear state: not optimal for performance but
// ensures there are no side effects of previous state
export const resetState = () => dispatch => {
  dispatch({ type: RESET_STATE });
};